import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { InsertRoofSubstructureBody } from "../../../../../http/patrol/types/roofSubstructures";
import { useRoofSubstructuresApi } from "../../../../../http/patrol/useRoofSubstructures";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface InsertRoofSubstructureForm {
  name: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  seamHeightRequired: boolean;
}

const NewRoofSubstructure: FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const roofSubstructuresApi = useRoofSubstructuresApi();

  const onSubmit = () => async (formData: InsertRoofSubstructureForm) => {
    const body: InsertRoofSubstructureBody = {
      name: formData.name!,
      nameDe: null,
      nameEs: null,
      nameFr: null,
      nameIt: null,
      order: formData.order!,
      visibility: formData.visibility,
      seamHeightRequired: formData.seamHeightRequired,
    };

    setIsSubmitting(true);

    try {
      const res = await roofSubstructuresApi.insertAdminRoofSubstructure(body);
      navigate(`/admin/systems/${systemId}/roof_substructures/${res.id}`);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<InsertRoofSubstructureForm>
      defaultValues={{
        name: null,
        order: 50,
        visibility: "hidden",
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, watch }) => {
        const seamHeightRequired = !!watch("seamHeightRequired");

        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isSubmitting}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.seamHeightRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={seamHeightRequired}
                  disabled={isSubmitting}
                  onChange={(checked) => setValue("seamHeightRequired", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={isSubmitting}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default NewRoofSubstructure;
